/**
 * Search Box — pixel-faithful port of `design/Search Box.dc.html`
 * (DC-RUNTIME §6.5).
 *
 * Contract notes for the screen lanes:
 *  - **Controlled/uncontrolled duality is part of the contract**: `value ??
 *    internal state`, `onChange ?? internal setter`. Passing neither gives a
 *    working uncontrolled input; passing both wires it to the store.
 *  - `onChange` receives the raw React change event (the DC runtime's
 *    `onChange` fired per keystroke, exactly like React's). Read
 *    `e.target.value`.
 *  - Placeholder colour comes from the app-global
 *    `input::placeholder{color:var(--gt-faint)}` rule in `styles/global.css`,
 *    not from this component.
 *  - The **Discover** search bar is NOT this component — the prototype inlines
 *    it so it can carry the 2px accent border, the glow shadow and the ✕ clear
 *    (INVENTORY §11). Do not substitute this one there.
 *
 * Prototype call sites: GOTY add-potential (`prefix="+"` `w="100%"` `ifs={12}`
 * `pad="13px 12px"`), Playtests search, filter-sheet tag search (`pfs={11}`),
 * browse sheet.
 */

import { useState } from 'react';
import type { ChangeEvent, CSSProperties, FocusEvent, Ref } from 'react';

export interface SearchBoxProps {
  /** DC default `'SEARCH…'` (the *page* default is `'SEARCH PLAYTESTS…'`) */
  placeholder?: string;
  /** any CSS width — DC default `'260px'` */
  w?: string;
  /** DC default `'▸'` */
  prefix?: string;
  /** prefix px — DC default 12 */
  pfs?: number | string;
  /** input px — DC default 11 */
  ifs?: number | string;
  /** DC default `'8px 12px'` */
  pad?: string;
  /** controlled value; omit for internal state */
  value?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  /**
   * Renders a ✕ inside the field that clears it (2026-08-28).
   *
   * PRESENT ONLY WHEN THERE IS SOMETHING TO CLEAR — a permanently-visible ✕ on
   * an empty box is a control that does nothing, and the field is narrow enough
   * (190px on the round rail) that the glyph is real estate. Passing no handler
   * keeps the old, clear-less box, which is what the four sheet call sites use.
   */
  onClear?: () => void;
  /**
   * Hands the host the underlying `<input>` — what the `/` hotkey focuses.
   *
   * Spread `useSearchInputRef()` from `desktop/searchHotkey.ts` here: the
   * hotkey registry is ref-attach driven, so a board that passes this becomes
   * `/`'s target while it is mounted and releases it on unmount, with no
   * screen-name list to keep in step.
   */
  inputRef?: Ref<HTMLInputElement>;
  /**
   * Fired when the field takes focus — which on a phone is the moment the
   * keyboard is about to cover the bottom of the screen. The filter sheet uses
   * it to bring the tag results up where they can still be read.
   */
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  /** The other half of `onFocus` — the sheet undoes its scroll room here. */
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  /** host-wrapper style — positional properties only */
  style?: CSSProperties;
}

export function SearchBox({
  placeholder,
  w,
  prefix,
  pfs,
  ifs,
  pad,
  value,
  onChange,
  onClear,
  inputRef,
  onFocus,
  onBlur,
  style,
}: SearchBoxProps) {
  const [inner, setInner] = useState('');
  const val = value ?? inner;
  const chg = onChange ?? ((e: ChangeEvent<HTMLInputElement>) => setInner(e.target.value));

  return (
    <div className="sc-host" data-sc-name="Search Box" style={style}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '9px',
          border: '1px solid var(--gt-border-2)',
          background: 'var(--gt-field)',
          padding: pad ?? '8px 12px',
          width: w ?? '260px',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <span style={{ fontFamily: 'var(--gt-font-mono)', fontSize: (pfs ?? 12) + 'px', color: 'var(--acc)' }}>
          <span>{prefix ?? '▸'}</span>
        </span>
        <input
          ref={inputRef}
          value={val}
          onChange={chg}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder={placeholder ?? 'SEARCH…'}
          style={{
            flex: 1,
            minWidth: 0,
            background: 'transparent',
            border: 0,
            outline: 'none',
            color: 'var(--gt-text)',
            fontFamily: 'var(--gt-font-mono)',
            fontSize: (ifs ?? 11) + 'px',
            padding: 0,
          }}
        />
        {onClear && val ? (
          <span
            onClick={onClear}
            data-tip="CLEAR THE SEARCH"
            style={{
              fontFamily: 'var(--gt-font-mono)',
              fontSize: (ifs ?? 11) + 'px',
              lineHeight: 1,
              color: 'var(--gt-muted)',
              cursor: 'pointer',
              flex: 'none',
            }}
          >
            ✕
          </span>
        ) : null}
      </div>
    </div>
  );
}
